import { Form } from "@/components/elements/Form";
import { useForm, useWatch } from "react-hook-form";
import { TCompanyExist, TJobForm } from "@/features/job/types/JobForm";
import { yupResolver } from "@hookform/resolvers/yup";
import { useGetJobSchema } from "@/features/job/hooks/useGetJobSchema";
import { createJobSalarySummaryForm, initJobWorkingTimeForm } from "@/features/job/utils/convertJobFormData";
import { JOB_STATUS, YES_NO_PENDING_STATUS } from "@/types/enum";
import { DEFAULT_ONBOARDING_SUPPORTS, DEFAULT_SELF_EXPENSES } from "@/features/job/constants/defaults";
import { JobFormTitle } from "@/features/job/components/job-form/JobFormTitle";
import { Grid, Stack, Typography } from "@mui/material";
import { FormField } from "@/components/elements/FormField";
import { Combobox } from "@/components/elements/Combobox";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { black, blue, orange } from "@/theme/colors";
import { useCompanyList } from "@/api/services/main/company";
import { useVisible } from "@/hooks/useVisible";
import { CreateCompanyModal } from "@/features/job/components/job-form/CreateCompanyModal";
import { Input } from "@/components/elements/Input";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Select } from "@/components/elements/Select";
import { useGetCompanySpecificIndustryList, useGetNationalityList } from "@/api/services/global/master";
import { IconWarning } from "@/assets/icons";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { JobEmploymentCondition } from "@/features/job/components/job-form/JobEmploymentCondition";
import { JobWorkingCondition } from "@/features/job/components/job-form/JobWorkingCondition";
import { useEnum } from "@/api/services/global/enum";
import { Textarea } from "@/components/elements/Textarea";
import { SelectMulti } from "@/components/elements/SelectMulti";
import { RadioGroup } from "@/components/elements/RadioGroup";
import { JOB_GENDER_OPTIONS } from "@/features/job/constants/enum";
import { dayjs } from "@/utils/dayjs";
import { useGetUserListPersonInCharge } from "@/api/services/main/user";
import { DuplicationCompanyScan } from "@/features/job/components/job-scan-image/DuplicationCompanyScan";
import { TGetCompanyOutputDto } from "@/api/services/main/company/dtos/get-company-list.dto";
import { TOption } from "@/types/common";
import { useGetMatchInfo } from "@/api/services/main/match";
import { useMe } from "@/api/services/main/auth";

/**
 * Filter companies for scan form dropdown.
 * Priority: 1) Match by company name, 2) Fallback to prefecture match, 3) Show all
 */
const filterCompaniesForScan = (
  companies: TGetCompanyOutputDto[] | undefined,
  scannedCompanyName: string | undefined,
  scannedPrefecture: string | undefined,
): TOption[] => {
  if (!companies || companies.length === 0) {
    return [];
  }

  const trimmedName = scannedCompanyName?.trim() ?? "";

  // Step 1: Try to match by company name
  if (trimmedName) {
    const nameMatched = companies.filter((c) => c.companyName?.trim() === trimmedName);
    if (nameMatched.length > 0) {
      return nameMatched.map((c) => ({ label: c.companyName ?? "", value: c.id }));
    }
  }

  // Step 2: Fallback to prefecture match
  if (scannedPrefecture) {
    const prefectureMatched = companies.filter((c) => c.prefecture === scannedPrefecture);
    if (prefectureMatched.length > 0) {
      return prefectureMatched.map((c) => ({
        label: c.companyName ? `${c.companyName} (${c.prefecture})` : "",
        value: c.id,
      }));
    }
  }

  // No matches found, return all companies
  return companies.map((c) => ({ label: c.companyName ?? "", value: c.id }));
};

type TCreateJobScanForm = {
  defaultValue?: Partial<TJobForm>;
  scannedCompanyName?: string;
  scannedPrefecture?: string;
  // True when BE resolved companySpecificIndustryId via fuzzy match (AI label ≠ master DB label).
  // Drives the「データベースと抽出結果に差異があります。」warning under the 分野 dropdown.
  companySpecificIndustryMapped?: boolean;
};

export type TCreateJobScanFormRef = {
  validateAndGetData: () => Promise<{ isValid: boolean; data: TJobForm | null }>;
};

export const CreateJobScanForm = forwardRef<TCreateJobScanFormRef, TCreateJobScanForm>(
  ({ defaultValue, scannedCompanyName, scannedPrefecture, companySpecificIndustryMapped }, ref) => {
    const [enableBlocker, setEnableBlocker] = useState(true);
    const [companyExists, setCompanyExists] = useState<TCompanyExist[]>(defaultValue?.companyExists ?? []);
    const { jobSchema } = useGetJobSchema();

    const deferredEnumValuesRef = useRef<{
      employmentType?: TJobForm["employmentType"];
      salaryType?: TJobForm["salaryType"];
      japaneseLevelType?: TJobForm["japaneseLevelType"];
    }>({
      employmentType: defaultValue?.employmentType,
      salaryType: defaultValue?.salaryType,
      japaneseLevelType: defaultValue?.japaneseLevelType,
    });

    // Create defaultValues without enum-dependent fields to prevent out-of-range warnings
    const { employmentType: _et, salaryType: _st, japaneseLevelType: _jlt, ...safeDefaultValue } = defaultValue ?? {};
    const defaultValues: Partial<TJobForm> = {
      jobWorkingTimes: [initJobWorkingTimeForm],
      jobSalarySummaries: [createJobSalarySummaryForm()],
      bonus: YES_NO_PENDING_STATUS.PENDING,
      salaryRaise: YES_NO_PENDING_STATUS.PENDING,
      selfExpense: DEFAULT_SELF_EXPENSES,
      onboardingSupport: DEFAULT_ONBOARDING_SUPPORTS,
      ...safeDefaultValue,
    };
    const methods = useForm<TJobForm>({
      defaultValues,
      resolver: yupResolver(jobSchema),
      mode: "all",
      reValidateMode: "onBlur",
    });
    const { control, setValue, trigger, getValues } = methods;

    useImperativeHandle(ref, () => ({
      validateAndGetData: async () => {
        const isValid = await trigger();
        if (isValid) {
          // Disable blocker before returning valid data
          setEnableBlocker(false);
          return { isValid: true, data: getValues() };
        }

        // Scroll to first error field
        const firstErrorElement = document.querySelector('[aria-invalid="true"]');
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return { isValid: false, data: null };
      },
    }));
    const recruiterId = useWatch({ control, name: "recruiterId" });
    const { userOptions, isLoading: isLoadingUserList } = useGetUserListPersonInCharge(recruiterId);

    const {
      data: companyListData,
      isLoading: isLoadingCompanyList,
      refetch: refetchCompanyList,
    } = useCompanyList({ getCompanyArchiveById: defaultValues?.companyId });
    const [companySearchInput, setCompanySearchInput] = useState(scannedCompanyName ?? "");

    const companyOptions = useMemo(() => {
      if (!companyListData?.data || companyListData.data.length === 0) {
        return [];
      }

      if (!companySearchInput || companySearchInput.trim() === "") {
        return companyListData.data.map((c) => ({
          label: c.prefecture ? `${c.companyName ?? ""} (${c.prefecture})` : (c.companyName ?? ""),
          value: c.id,
        }));
      }

      const match = companySearchInput.match(/^(.+?)\s*\(([^)]+)\)$/);
      const searchName = match ? match[1].trim() : companySearchInput.trim();
      const searchPrefecture = match ? match[2].trim() : undefined;

      let filtered = companyListData.data;

      if (searchName) {
        const nameMatched = filtered.filter((c) => c.companyName?.trim().includes(searchName));
        if (nameMatched.length > 0) {
          filtered = nameMatched;
        }
      }

      if (searchPrefecture) {
        const prefectureMatched = filtered.filter((c) => c.prefecture?.includes(searchPrefecture));
        if (prefectureMatched.length > 0) {
          filtered = prefectureMatched;
        }
      }

      return filtered.map((c) => ({
        label: c.prefecture ? `${c.companyName ?? ""} (${c.prefecture})` : (c.companyName ?? ""),
        value: c.id,
      }));
    }, [companyListData?.data, companySearchInput]);

    const { companySpecificIndustryOptions, isLoading: isLoadingCompanySpecificIndustry } = useGetCompanySpecificIndustryList();
    const { data: matchInfoResponse } = useGetMatchInfo();
    const { data: meResult } = useMe();

    const matchOfficeOptions = useMemo(() => {
      const options: TOption[] = [];
      const matchOffices = matchInfoResponse?.data?.matchOffices;
      if (matchOffices && Array.isArray(matchOffices)) {
        const sortedOffices = [...matchOffices].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
        sortedOffices.forEach((office) => {
          if (office?.id && office?.officeName && office.officeName.trim() !== "") {
            options.push({ label: office.officeName, value: office.id });
          }
        });
      }
      return options;
    }, [matchInfoResponse]);

    useEffect(() => {
      const currentRecruiterId = getValues("recruiterId");
      if (currentRecruiterId) return;

      const userData = meResult?.data;
      if (userData?.id && userData?.isMatchUser) {
        const parsedUserId = Number(userData?.id);
        if (!isNaN(parsedUserId)) {
          setValue("recruiterId", parsedUserId);
        }
      }
    }, [meResult, setValue, getValues]);
    const { data: japaneseLevelTypeOptions, isSuccess: isJapaneseLevelLoaded } = useEnum("EJapaneseLevelType");
    const { data: recruitmentLocationTypeOptions } = useEnum("ERecruitmentLocationType");
    const { data: jobResidenceStatusOptions } = useEnum("EJobResidenceStatus");
    const { nationalityOptions, isLoading: isLoadingNationality } = useGetNationalityList();
    const { data: employmentTypeOptions, isSuccess: isEmploymentTypeLoaded } = useEnum("EEmploymentType");
    const { data: salaryTypeOptions, isSuccess: isSalaryTypeLoaded } = useEnum("ESalaryType");
    const companyIdWatchField = useWatch({
      control,
      name: "companyId",
    });

    useEffect(() => {
      if (companySearchInput === "" || companySearchInput === null || companySearchInput === undefined || companySearchInput !== scannedCompanyName) {
        setCompanyExists([]);
      } else {
        setCompanyExists(defaultValue?.companyExists ?? []);
      }
    }, [companyIdWatchField]);

    const createCompanyModal = useVisible();

    // Auto-preselect 企業名 when the scanned company name matches exactly ONE existing
    // company (exact trimmed name). Ambiguous (>1 same-name) or no match → leave empty
    // so the user picks from the dropdown. Runs once when the list first loads; never
    // overrides a value the user already chose.
    const companyAutoSelectedRef = useRef(false);
    useEffect(() => {
      if (companyAutoSelectedRef.current) return;
      const trimmedName = scannedCompanyName?.trim();
      if (!trimmedName) return;
      const companies = companyListData?.data;
      if (!companies || companies.length === 0) return;
      if (getValues("companyId")) {
        companyAutoSelectedRef.current = true;
        return;
      }
      const exactMatches = companies.filter((c) => c.companyName?.trim() === trimmedName);
      if (exactMatches.length === 1) {
        // Set the same value the dropdown option carries (numeric id) so the Combobox's
        // strict-equality option lookup resolves and the selection renders. Mirrors what
        // a manual pick does (option.value → field.onChange). Cast because TJobForm types
        // companyId as string, though the option value is numeric at runtime.
        setValue("companyId", exactMatches[0].id as unknown as string, { shouldValidate: true });
      }
      companyAutoSelectedRef.current = true;
    }, [companyListData?.data, scannedCompanyName, setValue, getValues]);

    useEffect(() => {
      setValue("status", JOB_STATUS.DRAFT);
    }, []);

    useEffect(() => {
      const deferred = deferredEnumValuesRef.current;
      if (isEmploymentTypeLoaded && deferred.employmentType && employmentTypeOptions.some((opt) => opt.value === deferred.employmentType)) {
        setValue("employmentType", deferred.employmentType);
        deferred.employmentType = undefined;
      }
    }, [isEmploymentTypeLoaded, employmentTypeOptions, setValue]);

    useEffect(() => {
      const deferred = deferredEnumValuesRef.current;
      if (isSalaryTypeLoaded && deferred.salaryType && salaryTypeOptions.some((opt) => opt.value === deferred.salaryType)) {
        setValue("salaryType", deferred.salaryType);
        deferred.salaryType = undefined;
      }
    }, [isSalaryTypeLoaded, salaryTypeOptions, setValue]);

    useEffect(() => {
      const deferred = deferredEnumValuesRef.current;
      if (isJapaneseLevelLoaded && deferred.japaneseLevelType && japaneseLevelTypeOptions.some((opt) => opt.value === deferred.japaneseLevelType)) {
        setValue("japaneseLevelType", deferred.japaneseLevelType);
        deferred.japaneseLevelType = undefined;
      }
    }, [isJapaneseLevelLoaded, japaneseLevelTypeOptions, setValue]);

    const onSubmit = () => {};
    const handleCompanyCreated = (companyId: string) => {
      setValue("companyId", companyId, { shouldValidate: true });
    };

    return (
      <>
        <Form methods={methods} onSubmit={onSubmit} schema={jobSchema} enableBlocker={enableBlocker}>
          <JobFormTitle text="基本情報" />
          <Grid container mb={3} mt={0} spacing={2}>
            <Grid item xs={12}>
              <FormField control={control} name="companyId">
                <Combobox
                  options={companyOptions}
                  loading={isLoadingCompanyList}
                  initialInputValue={scannedCompanyName ?? ""}
                  onInputChange={(newValue) => {
                    setCompanySearchInput(newValue);
                    if (!newValue || newValue.trim() === "") {
                      setCompanyExists([]);
                    }
                  }}
                />
              </FormField>
              <Stack direction="row" alignItems="center" mt={1}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{
                    cursor: companyIdWatchField ? "not-allowed" : "pointer",
                    opacity: companyIdWatchField ? 0.5 : 1,
                    pointerEvents: companyIdWatchField ? "none" : "auto",
                  }}
                  onClick={createCompanyModal.open}
                >
                  <AddCircleOutlineIcon
                    sx={{
                      color: companyIdWatchField ? black[60] : blue[80],
                      fontSize: 18,
                    }}
                  />
                  <Typography variant="cap12">新規企業登録</Typography>
                </Stack>
              </Stack>
              {companyExists && companyExists.length > 0 && (
                <>
                  <DuplicationCompanyScan listCompanyExists={companyExists} />
                  <Stack direction="row" mt={1}>
                    <IconWarning color={orange[80]} />
                    <Typography variant="cap12" color={orange[80]}>
                      既に同名の企業が登録されています。この企業でお間違いないかご確認の上、登録してください。
                    </Typography>
                  </Stack>
                </>
              )}
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="jobName">
                <Input placeholder="入力してください" />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="recruitmentNumber">
                <Input placeholder="入力してください" endAdornment="人" clearable={false} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="matchOfficeId">
                <Select placeholder="選択してください" options={matchOfficeOptions} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="recruiterId">
                <Select placeholder="選択してください" options={userOptions} loading={isLoadingUserList} />
              </FormField>
            </Grid>

            <Grid item xs={6}>
              <FormField control={control} name="companySpecificIndustryId">
                <Select
                  onChange={() => {
                    setValue("companySpecificBusinessClassificationIds", [], {
                      shouldValidate: true,
                    });
                    setValue("specificSkillCertificateIds", [], {
                      shouldValidate: true,
                    });
                  }}
                  placeholder="選択してください"
                  loading={isLoadingCompanySpecificIndustry}
                  options={companySpecificIndustryOptions}
                />
                {companySpecificIndustryMapped && (
                  <Stack direction="row" mt={0.5}>
                    <IconWarning color={orange[80]} />
                    <Typography variant="cap12" color={orange[80]}>
                      データベースと抽出結果に差異があります。
                    </Typography>
                  </Stack>
                )}
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="startDate">
                <InputDatePicker minDate={dayjs("1900/01/01")} maxDate={dayjs("2099/12/31")} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="endDate">
                <InputDatePicker minDate={dayjs("1900/01/01")} maxDate={dayjs("2099/12/31")} />
              </FormField>
            </Grid>
          </Grid>
          <JobEmploymentCondition bordered={false} isDraft={true} />
          <JobWorkingCondition bordered={false} isDraft={true} />
          <JobFormTitle text="応募要件" />
          <Grid container mb={3} mt={0} spacing={2}>
            <Grid item xs={6}>
              <FormField control={control} name="japaneseLevelType">
                <Select placeholder="選択してください" options={japaneseLevelTypeOptions} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="japaneseLevelOther">
                <Input placeholder="入力してください" />
              </FormField>
            </Grid>
            <Grid item xs={12}>
              <FormField control={control} name="skillsExperience">
                <Textarea />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="ageLimit">
                <Input placeholder="入力してください" />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="nationalityIds">
                <SelectMulti placeholder="選択してください" loading={isLoadingNationality} options={nationalityOptions} separator="、" />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="recruitmentLocationType">
                <Select placeholder="選択してください" options={recruitmentLocationTypeOptions} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="desiredHiringPeriod">
                <Input placeholder="入力してください" />
              </FormField>
            </Grid>
            <Grid item xs={12}>
              <FormField control={control} name="gender">
                <RadioGroup direction="row" options={JOB_GENDER_OPTIONS} />
              </FormField>
            </Grid>
            <Grid item xs={12}>
              <FormField control={control} name="residenceStatusIds">
                <SelectMulti placeholder="選択してください" options={jobResidenceStatusOptions} separator="、" />
              </FormField>
            </Grid>
          </Grid>
        </Form>
        <CreateCompanyModal modal={createCompanyModal} refetchCompanyList={refetchCompanyList} onCompanyCreated={handleCompanyCreated} />
      </>
    );
  },
);
